import { Poll } from 'schema/poll';
import { CompositeQuestionProps, QuestionProps } from 'src/types/poll.domain';
import { WrapperProps } from '../root/wrapper';

const AuthStep = () => import('./questions/AuthPoll').then(v => v.AuthPoll);
const ChoiceConjoint = () => import('./questions/ChoiceConjoint').then(v => v.ChoiceConjoint);
const ClickMapPoll = () => import('./questions/ClickMapPoll').then(v => v.ClickMapPoll);
const FilePoll = () => import('./questions/FilePoll').then(v => v.FilePoll);
const MatrixMultiplePoll = () => import('./questions/Matrix/MatrixMultiplePoll').then(v => v.MatrixMultiplePoll);
const MatrixRatingDigitsPoll = () =>
  import('./questions/Matrix/MatrixRatingDigitsPoll').then(v => v.MatrixRatingDigitsPoll);
const MatrixRatingStarsPoll = () =>
  import('./questions/Matrix/MatrixRatingStarsPoll').then(v => v.MatrixRatingStarsPoll);
const MatrixSinglePoll = () => import('./questions/Matrix/MatrixSinglePoll').then(v => v.MatrixSinglePoll);
const MaxDiffPoll = () => import('./questions/MaxDiffPoll').then(v => v.MaxDiffPoll);
const MediaFileAny = () => import('./questions/MediaFileAny/MediaFileAny').then(v => v.MediaFileAny);
const MediaFileAudio = () => import('./questions/MediaFileAudio').then(v => v.MediaFileAudio);
const MediaFileImage = () => import('./questions/MediaFileImage').then(v => v.MediaFileImage);
const MediaFileText = () => import('./questions/MediaFileText').then(v => v.MediaFileText);
const MediaFileVideo = () => import('./questions/MediaFileVideo').then(v => v.MediaFileVideo);
const MultipleFromPicturePoll = () =>
  import('./questions/MultipleFromPicturePoll/MultipleFromPicturePoll').then(v => v.MultipleFromPicturePoll);
const MultiplePoll = () => import('./questions/MultiplePoll').then(v => v.MultiplePoll);
const MultipleSelectPoll = () => import('./questions/MultipleSelectPoll').then(v => v.MultipleSelectPoll);
const RankPoll = () => import('./questions/RankPoll').then(v => v.RankPoll);
const RatingDigitsPoll = () => import('./questions/RatingDigitsPoll').then(v => v.RatingDigitsPoll);
const RatingLikePoll = () => import('./questions/RatingLikePoll').then(v => v.RatingLikePoll);
const RatingSliderPoll = () => import('./questions/RatingSliderPoll').then(v => v.RatingSliderPoll);
const RatingSmiliesPoll = () => import('./questions/RatingSmilesPoll').then(v => v.RatingSmilesPoll);
const RatingStarsPoll = () => import('./questions/RatingStarsPoll').then(v => v.RatingStarsPoll);
const SelectPoll = () => import('./questions/SelectPoll/SelectPoll').then(v => v.SelectPoll);
const SingleFromPicturePoll = () =>
  import('./questions/SingleFromPicturePoll/SingleFromPicturePoll').then(v => v.SingleFromPicturePoll);
const SinglePoll = () => import('./questions/SinglePoll').then(v => v.SinglePoll);
const SwitchPoll = () => import('./questions/SwitchPoll').then(v => v.SwitchPoll);
const BinaryPoll = () => import('./questions/BinaryPoll').then(v => v.BinaryPoll);
const TextareaPoll = () => import('./questions/TextareaPoll/TextareaPoll').then(v => v.TextareaPoll);
const CardSortingCategoryPoll = () =>
  import('./questions/CardSortingCategoryPoll/CardSortingCategoryPoll').then(v => v.CardSortingCategoryPoll);

const PrototypeScreen = () => import('./questions/PrototypeScreen').then(v => v.PrototypeScreen);

type CompositeQuestionTypes = 'POLL_CARD_SORTING_CATEGORY';

export const asyncQuestionComponentLoaders: Record<
  Exclude<Poll['questions'][number]['data']['type'], CompositeQuestionTypes>,
  () => Promise<React.FC<QuestionProps & { groupId?: string | undefined } & Omit<WrapperProps, 'children'>>>
> = {
  MEDIA_FILE_AUDIO: MediaFileAudio,
  MEDIA_FILE_TEXT: MediaFileText,
  MEDIA_FILE_VIDEO: MediaFileVideo,
  POLL_TEXTAREA: TextareaPoll,
  POLL_PROTOTYPE_SCREEN: PrototypeScreen,
  POLL_SINGLE: SinglePoll,
  POLL_MAXDIFF: MaxDiffPoll,
  POLL_CHOICE_CONJOINT: ChoiceConjoint,
  POLL_MULTIPLE: MultiplePoll,
  POLL_SINGLE_FROM_PICTURE: SingleFromPicturePoll,
  POLL_MULTIPLE_FROM_PICTURE: MultipleFromPicturePoll,
  POLL_RANK: RankPoll,
  POLL_RATING_DIGITS: RatingDigitsPoll,
  POLL_RATING_LIKE: RatingLikePoll,
  POLL_RATING_SLIDER: RatingSliderPoll,
  POLL_RATING_SMILIES: RatingSmiliesPoll,
  POLL_RATING_STARS: RatingStarsPoll,
  POLL_SELECT: SelectPoll,
  POLL_MULTIPLE_SELECT: MultipleSelectPoll,
  MATRIX_POLL_SINGLE: MatrixSinglePoll,
  MATRIX_POLL_MULTIPLE: MatrixMultiplePoll,
  MATRIX_POLL_RATING_DIGITS: MatrixRatingDigitsPoll,
  MATRIX_POLL_RATING_STARS: MatrixRatingStarsPoll,
  POLL_CLICK_MAP: ClickMapPoll,
  POLL_MARKUP_AREA: () => Promise.resolve(() => null), // deprecated
  POLL_SELECT_AREA: () => Promise.resolve(() => null), // deprecated
  POLL_FILE: FilePoll,
  MEDIA_FILE_ANY: MediaFileAny,
  MEDIA_FILE_IMAGE: MediaFileImage,
  MEDIA_FILE_LINK: () => Promise.resolve(() => null), // заменяется реальными медиафайлами в pollPreStartTransformer
  POLL_AUTH: AuthStep,
  POLL_SWITCH: SwitchPoll,
  POLL_BINARY: BinaryPoll,
};

export const asyncCompositeQuestionComponentLoaders: Record<
  Extract<Poll['questions'][number]['data']['type'], CompositeQuestionTypes>,
  () => Promise<React.FC<CompositeQuestionProps & Omit<WrapperProps, 'children'>>>
> = {
  POLL_CARD_SORTING_CATEGORY: CardSortingCategoryPoll,
};
